import * as React from "react"
import * as Breast from "models/breast";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Link } from "react-scroll";
import { FilterContext } from "models/core/filter";
import { DashChip } from "components/common/DashChips";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { Download, Downloading } from "@mui/icons-material";
import { BreastIndicatorDefinitionsUrl } from "config/appUrlConfig";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum BreastIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: BreastIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: BreastIndicatorAction.DownloadIndicatorSuccess }
	| { type: BreastIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number,
	downloading: boolean
}

const initialState: State = {
	indicator: 0,
	downloading: false
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case BreastIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload, downloading: true } };

		case BreastIndicatorAction.DownloadIndicatorSuccess:
		case BreastIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0, downloading: false } };

		default:
			return state;
	}
}

let stateWideData: {
	title?: string;
	invasive?: string;
	hadSurgery?: string;
	slnbOnT1TumoursWithIndexBCS?: string;
	slnbAndAxillaryLymphNodeDissection?: string;
	axillaryLymphNodeDiessection?: string;
	definitiveMastectomy?: string;
	mastectomyForT1Tumours?: string;
	reExcisionOfLesionAfterIndexBCS?: string;
	convertionOfIndexBCSToMastectomy?: string;
	timeFromPathologicalDiagnosisToFirstSurgery45Days?: string;
	adjuvantIVSTForAxillaryLymphNodePositivePatientsUnder70?: string;
	neoadjuvantTherapy?: string;
	timeFromDefinitiveSurgeryToIVST45Days?: string;
	xrtFollowingDefinitiveBCS?: string;
	timeFromDefinitiveSurgeryToXrt60Days?: string;
	immediateBreastReconstructionSurgery?: string;
	delayedBreastReconstructionSurgery?: string;
	timeToTreatmentCompletion37Weeks?: string;
} = {};

export function BreastDashboard() {
	const [localState, localDispatch] = React.useReducer(reducer, initialState)
	const { state, dispatch } = React.useContext(AppContext);

	const [showFilterChips, setFilterChips] = React.useState(true);

	
	const addFilter: Breast.AddFilterFunc = async (key: keyof typeof Breast.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
			const filteredDashboard = await Breast.computeChartData(Breast.addFilter(state.page.dashboard as Breast.IBreastDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
			dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
		}
	
		const removeFilter: Breast.RemoveFilterFunc = async (key: keyof typeof Breast.DashboardFilterKeys, value?: string) => {
			const filteredDashboard = await Breast.computeChartData(Breast.removeFilter(state.page.dashboard as Breast.IBreastDashboard, key, value));
			dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
		}
	
		React.useEffect(() => {
			dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
	
			const fetchStateReferenceData = async () => {
				stateWideData = await getStateIndicatorReferences("breast", 2022);
			}
	
			const loadDashboard = async () => {
				const value = await Breast.loadBreastDashboard(state.page.dashboard as Breast.IBreastDashboard);
				dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value });
			}
	
			Promise.all([fetchStateReferenceData(), loadDashboard()]);
	
			return () => {
				Breast.closeBreastDashboard();
			}
		}, []);
	
		React.useEffect(() => {
			if (localState.indicator == 0)
				return;
	
			let ids = breastChartData.level1.filteredReferralIdCsv;
			let alldata = false;
	
	
			const getBreastIndicatorPatientList = async () => {
				try {
					const data = await ReportApi.getBreastIndicatorPatientList(ids, alldata, localState.indicator);
					if (data.errorMessage) {
						console.error(data.errorMessage);
						localDispatch({ type: BreastIndicatorAction.DownloadIndicatorFail })
					}
					localDispatch({ type: BreastIndicatorAction.DownloadIndicatorSuccess })
					return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
				} catch {
					localDispatch({ type: BreastIndicatorAction.DownloadIndicatorFail })
				}
			}
	
			getBreastIndicatorPatientList();
	
	
		}, [localState.indicator]);
	
	
		if (state.page.loading || !state.page.dashboard.chartData)
			return <Loader />
	
	
		//Create function to show/hide filter chips
		const toggleChips = () => {
			if (showFilterChips) {
				setFilterChips(false);
			} else {
				setFilterChips(true);
			}
		};
	
		let activeFiltersArray = [];
		let filterArray = [];
		let chips = [];
	
		for (const key in state.page.dashboard.filters) {
			if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
				activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
			}
		}
	
		for (const key in Breast.DashboardFilterKeys) {
			if (Object.prototype.hasOwnProperty.call(Breast.DashboardFilterKeys, key)) {
				filterArray.push({ "key": key, "label": Breast.DashboardFilterKeys[key].label });
			}
		}
	
		activeFiltersArray.forEach(a => {
			a.values.forEach(b => {
				chips.push(<DashChip key={a.key + b}
					removeFilterFn={removeFilter}
					filterName={filterArray.find(id => id.key == a.key).label}
					filterValue={b}
					filterId={a.key}
					filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
				/>
				)
			})
		})
	
		const breastChartData = state.page.dashboard.chartData as Breast.IChartData;
	
		const downloadBtn = (extractTypeId: number) => {
			const { indicator } = localState;
	
			if (indicator == extractTypeId)
				return <Downloading />
	
			return (
				<Download style={{ cursor: "pointer" }} onClick={() =>
					localDispatch({ type: BreastIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
			)
		}
	
		const ecog = {
			numerator: 0,
			denominator: 0,
			percentage: 0
		}
	
		const stage = {
			numerator: 0,
			denominator: 0,
			percentage: 0
		}
	
		if (breastChartData.level1.totalDiagnoses > 0) {
			ecog.numerator = breastChartData.level1.totalDiagnoses - (breastChartData.level2.ecogGroup.values[breastChartData.level2.ecogGroup.labels.indexOf("Unk")] || 0);
			ecog.denominator = breastChartData.level1.totalDiagnoses
			ecog.percentage = ecog.numerator / ecog.denominator * 100
	
			stage.numerator = breastChartData.level1.totalDiagnoses - (breastChartData.level2.tnmStage.values[breastChartData.level2.tnmStage.labels.indexOf("Unk")] || 0);
			stage.denominator = breastChartData.level1.totalDiagnoses;
			stage.percentage = stage.numerator / stage.denominator * 100
		}
	
	
		const dashboard = <>
			<div style={{
				position: "sticky",
				top: "64px",
				left: "10px",
				zIndex: 2,
				backgroundColor: "white"
			}}>
	
	
				{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
					{chips}
				</div> : null)}
			</div>
	
			<div style={{
				maxWidth: 1000,
				margin: "0 auto"
			}}>
	
				<div style={{
					textAlign: "right",
					paddingBottom: "10px",
					paddingTop: "10px",
					position: "sticky",
					zIndex: 1,
					top: "60px",
					backgroundColor: "white"
				}}>
					<Button onClick={() => localDispatch({ type: BreastIndicatorAction.UserClickedDownloadIndicator, payload: 99 })}
						style={ localState.downloading && {backgroundColor: "grey", color: "white"} || {
							backgroundColor: (breastChartData.level1.totalDiagnoses > 0 ? ("#1e88e5") : ("#d3d3d3")),
							color: "white",
							alignItems: "right"
						}}>
						Download Patient Details
					</Button>
				</div>

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell align="center">My MDT(s)*</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.title}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Female invasive breast cancer diagnoses</TableCell>
							<TableCell align="center">{breastChartData.level1.totalInvasive}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.invasive}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Percentage of female patients with invasive breast cancers with ECOG</TableCell>
							<TableCell align="center">{`${ecog.percentage.toFixed(0)}%`} <br /> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Percentage of female patients with invasive breast cancers with stage</TableCell>
							<TableCell align="center">{`${stage.percentage.toFixed(0)}%`} <br /> {`(${stage.numerator}/${stage.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Number of female patients with invasive breast cancers who had breast surgery</TableCell>
							<TableCell align="center">{`${breastChartData.level1.hadSurgery.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.hadSurgery.numerator}/${breastChartData.level1.hadSurgery.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgery}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}><b>Diagnosis & Staging - Diagnosis and staging are crucial for breast cancer treatment planning</b></TableCell>
						</TableRow>
						<TableRow>
							<TableCell>SLNB on a T1 tumour with index BCS</TableCell>
							<TableCell align="center">{`${breastChartData.level1.slnbOnT1TumoursWithIndexBCS.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.slnbOnT1TumoursWithIndexBCS.numerator}/${breastChartData.level1.slnbOnT1TumoursWithIndexBCS.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.slnbOnT1TumoursWithIndexBCS}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>SLNB and axillary lymph node dissection (ALND)</TableCell>
							<TableCell align="center">{`${breastChartData.level1.slnbAndAxillaryLymphNodeDissection.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.slnbAndAxillaryLymphNodeDissection.numerator}/${breastChartData.level1.slnbAndAxillaryLymphNodeDissection.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.slnbAndAxillaryLymphNodeDissection}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Axillary lymph node dissection (ALND)</TableCell>
							<TableCell align="center">{`${breastChartData.level1.axillaryLymphNodeDissection.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.axillaryLymphNodeDissection.numerator}/${breastChartData.level1.axillaryLymphNodeDissection.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.axillaryLymphNodeDiessection}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}>
								<b>Surgery - Surgery is usually the first type of treatment for early or locally advanced breast cancer</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Definitive Mastectomy</TableCell>
							<TableCell align="center">{`${breastChartData.level1.definitiveMastectomy.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.definitiveMastectomy.numerator}/${breastChartData.level1.definitiveMastectomy.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.definitiveMastectomy}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Mastectomy for T1 tumours</TableCell>
							<TableCell align="center">{`${breastChartData.level1.mastectomyForT1Tumour.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.mastectomyForT1Tumour.numerator}/${breastChartData.level1.mastectomyForT1Tumour.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mastectomyForT1Tumours}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Re-excision of lesion after index breast conserving surgery (BCS)</TableCell>
							<TableCell align="center">{`${breastChartData.level1.reExcisionAfterIndexBCS.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.reExcisionAfterIndexBCS.numerator}/${breastChartData.level1.reExcisionAfterIndexBCS.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.reExcisionOfLesionAfterIndexBCS}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Conversion of index breast conserving surgery (BCS) to mastectomy</TableCell>
							<TableCell align="center">{`${breastChartData.level1.conversionOfIndexBCSToMastectomy.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.conversionOfIndexBCSToMastectomy.numerator}/${breastChartData.level1.conversionOfIndexBCSToMastectomy.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.convertionOfIndexBCSToMastectomy}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Time from pathological diagnosis to first surgery ≤ 45 days</TableCell>
							<TableCell align="center">{`${breastChartData.level1.timeFromPathologicalDiagnosisToFirstSurgery45Days.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.timeFromPathologicalDiagnosisToFirstSurgery45Days.numerator}/${breastChartData.level1.timeFromPathologicalDiagnosisToFirstSurgery45Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.timeFromPathologicalDiagnosisToFirstSurgery45Days}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}>
								<b>Systemic Therapy - Systemic therapy may be used at different times to treat breast cancer</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Adjuvant IVST for axillary lymph node positive women aged under 70 years</TableCell>
							<TableCell align="center">{`${breastChartData.level1.adjuvantIVSTForAxillaryLNPositiveAgedLessThan70.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.adjuvantIVSTForAxillaryLNPositiveAgedLessThan70.numerator}/${breastChartData.level1.adjuvantIVSTForAxillaryLNPositiveAgedLessThan70.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.adjuvantIVSTForAxillaryLymphNodePositivePatientsUnder70}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Neoadjuvant therapy</TableCell>
							<TableCell align="center">{`${breastChartData.level1.neoadjuvantIVST.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.neoadjuvantIVST.numerator}/${breastChartData.level1.neoadjuvantIVST.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.neoadjuvantTherapy}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Time from definitive surgery to first IVST ≤ 45 days</TableCell>
							<TableCell align="center">{`${breastChartData.level1.timeFromDefinitiveSurgeryToFirstIVST45Days.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.timeFromDefinitiveSurgeryToFirstIVST45Days.numerator}/${breastChartData.level1.timeFromDefinitiveSurgeryToFirstIVST45Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.timeFromDefinitiveSurgeryToIVST45Days}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}>
								<b>Radiation Therapy - Radiotherapy is usually given after surgery and systemic therapy</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Radiation therapy following definitive breast conserving surgery (BCS)</TableCell>
							<TableCell align="center">{`${breastChartData.level1.xrtFollowingDefinitiveBCS.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.xrtFollowingDefinitiveBCS.numerator}/${breastChartData.level1.xrtFollowingDefinitiveBCS.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.xrtFollowingDefinitiveBCS}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Time from definitive surgery to first radiation therapy ≤ 60 days</TableCell>
							<TableCell align="center">{`${breastChartData.level1.daysToXrtStartDateFromDefinitiveSurgery.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.daysToXrtStartDateFromDefinitiveSurgery.numerator}/${breastChartData.level1.daysToXrtStartDateFromDefinitiveSurgery.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.timeFromDefinitiveSurgeryToXrt60Days}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}>
								<b>Accessibility - Making health services available in the most suitable setting</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Immediate breast reconstruction surgery</TableCell>
							<TableCell align="center">{`${breastChartData.level1.reconstructionAtMastectomy.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.reconstructionAtMastectomy.numerator}/${breastChartData.level1.reconstructionAtMastectomy.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.immediateBreastReconstructionSurgery}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Delayed breast reconstruction surgery</TableCell>
							<TableCell align="center">{`${breastChartData.level1.reconstructionAfterMastectomy.percentage.toFixed(1)}%`} <br /> {`(${breastChartData.level1.reconstructionAfterMastectomy.numerator}/${breastChartData.level1.reconstructionAfterMastectomy.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.delayedBreastReconstructionSurgery}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Time to treatment completion within 37 weeks from pathological diagnosis</TableCell>
							<TableCell align="center">{`${breastChartData.level1.timeToCompletionWithin37Weeks.percentage.toFixed(0)}%`} <br /> {`(${breastChartData.level1.timeToCompletionWithin37Weeks.numerator}/${breastChartData.level1.timeToCompletionWithin37Weeks.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.timeToTreatmentCompletion37Weeks}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Data based on facility of MDT</i> </p>
				<p><i>^The comparison data in the Qld report is based on a person's incidence case of the cancer. The MDT data is all diagnoses (with a diagnosis date) presented to MDT using the Qool application</i> </p>
			</div>

			<div style={{ textAlign: "center" }}>
				<p>This report is based on the <a href={BreastIndicatorDefinitionsUrl} target="_blank">Queensland Breast Cancer Quality Index 2013-2022.</a><br /> Indicator definitions and glossary can be found here also</p>
			</div>

		</div>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)


} 